export default class TextSelection {
  /**
   * Calculate the character offset from an ancestor element,
   * given the offset within a node
   * @param {Node} node           the node containing the text
   * @param {Element} ancestor       an ancestor of the node, a span or a tspan
   * @param {number} nodeCharOffset char offset within the node
   */
  static calculateCharOffsetFromAncestor(
    node,
    ancestor,
    nodeCharOffset,
    privateUse = { wasFound: false }
  ) {
    let children = ancestor.childNodes;
    let count = 0;
    for (let i = 0; i < children.length; ++i) {
      let child = children[i];
      if (child === node) {
        privateUse.wasFound = true;
        return count + nodeCharOffset;
      }
      if (child.hasChildNodes()) {
        let add = TextSelection.calculateCharOffsetFromAncestor(
          node,
          child,
          nodeCharOffset,
          privateUse
        );
        count += add;
        if (privateUse.wasFound) return count;
      } else {
        count += child.textContent.length;
      }
    }
    return count;
  }

  static applyTextMark(
    sourceGabc,
    symbol,
    remove = false,
    start = 0,
    end = Infinity
  ) {
    let { prefix, suffix, taggedText } = this.getPrefixSuffixForTaggedText(
      sourceGabc,
      start,
      end
    );
    return (
      prefix +
      TextSelection.removeDiacriticalMarks(taggedText) +
      (remove ? "" : symbol) +
      suffix
    ).normalize('NFC');
  }

  static getPrefixSuffixForTaggedText(taggedText, start = 0, end = Infinity) {
    let reStart = new RegExp(`(?:(?:<\\/?\\w+>)*(.)){${start}}`),
      prefix = taggedText.match(reStart),
      suffix = "";
    if (prefix) {
      prefix = prefix[0];
    } else {
      prefix = "";
      start = 0;
    }
    taggedText = taggedText.slice(prefix.length);

    if (end < Infinity && end - start >= 0) {
      let reEnd = new RegExp(`(?:(?:<\\/?\\w+>)*(.)){${end - start}}`);
      suffix = taggedText.match(reEnd);
      if (suffix) {
        let suffixLength = suffix[0].length;
        suffix = taggedText.slice(suffixLength);
        taggedText = taggedText.slice(0, suffixLength);
      }
    }
    return {
      prefix,
      suffix,
      taggedText
    };
  }

  static replace(sourceGabc, replace, activeTags, start, end) {
    let { prefix, suffix, taggedText } = this.getPrefixSuffixForTaggedText(
      sourceGabc,
      start,
      end
    );
    replace = (taggedText.match(/(<[^>]+>)+/g) || []).join("") + replace;
    return TextSelection.reduceTags(prefix + replace + suffix);
  }

  /**
   * Apply a format to a gabc text string or substring
   * @param  {string} sourceGabc      old GABC
   * @param  {string} tagName         tag to apply
   * @param  {boolean} [remove=false] whether to remove the tag
   * @param  {Number} [start=0]       start of substring
   * @param  {Number} [end=Infinity]  end of substring
   * @return {string}                 new GABC
   */
  static applyFormatTag(
    sourceGabc,
    tagName,
    remove = false,
    start = 0,
    end = Infinity
  ) {
    let { prefix, suffix, taggedText } = this.getPrefixSuffixForTaggedText(
      sourceGabc,
      start,
      end
    );
    let closeTag = "/" + tagName;
    tagName = [tagName, closeTag];
    if (remove) tagName.reverse();
    let newGabc = `${prefix}<${tagName[0]}>${taggedText}<${tagName[1]}>${suffix}`;
    return TextSelection.reduceTags(newGabc);
  }

  static reduceTags(taggedText) {
    // first replace any closing tags that are immediately adjacent to opening tags:
    let reAdjacentTags = /<\/(\w+)>((?:<[^>]*>)*)<\1>|<(\w+)>((?:<[^>]*>)*)<\/\3>|(?:<\w+>)+$/g;
    let taggedTextLength = taggedText.length;
    while (
      (taggedText = taggedText.replace(reAdjacentTags, "$2$4")).length <
      taggedTextLength
    ) {
      taggedTextLength = taggedText.length;
    }
    // now replace nested tags:
    let reNestedTags = /(<(\w+)>(?:[^<]|<(?!\/?\2))*)<\2>(.*?)(?:<\/\2>|$)/g;
    while (
      (taggedText = taggedText.replace(reNestedTags, "$1$3")).length <
      taggedTextLength
    ) {
      taggedTextLength = taggedText.length;
    }
    // now replace any closing tags that have already been closed:
    let reExcessiveClosingTags = /(<\/(\w+)>(?:[^<]|<(?!\/?\2))+)<\/\2>/g;
    while (
      (taggedText = taggedText.replace(reExcessiveClosingTags, "$1")).length <
      taggedTextLength
    ) {
      taggedTextLength = taggedText.length;
    }
    // now replace any closing tags that come before an opening tag:
    // negative lookbehind is only supported by chrome right now?
    // let reMoreExcessiveClosingTags = /<\/(\w+)>(?<!<\1>(?:[^<]|<\1>|<)+<\/\1>)/g;

    // while (
    //   (taggedText = taggedText.replace(reMoreExcessiveClosingTags, "")).length <
    //   taggedTextLength
    // ) {
    //   taggedTextLength = taggedText.length;
    // }
    return taggedText;
  }

  static isCombinationCode(text) {
    return /^[\u0300-\u03ff]+$/.test(text);
  }

  static getCombinationCodesAtBeginning(text) {
    return /^[\u0300-\u03ff]*/.exec(text)[0];
  }

  /**
   * 
   * @param {string} text 
   * @returns 
   */
  static removeDiacriticalMarks(text) {
    if (!text) return text;
    return text.normalize('NFD').replace(/[\u0300-\u03ff]+/g, "");
  }

  /**
   * returns text if it is a single character (whether made up of combining characters or not)
   * @param  {string} text the string to work on
   * @return {string}      the string itself, if it is a single character, or null otherwise
   */
  static getSingleChar(text) {
    if (!text) return text;
    switch (text.length) {
      case 1:
        return text;
      default:
        let match = /^([a-zæœ])[\u0300-\u03ff]+$/i.exec(text);
        return match ? text : null;
    }
  }
}

TextSelection.Marks = [
  {
    sym: "\u0301",
    key: "acute",
    reNone: /[aeiouyæœ]/i,
    reWith: /[áéíóúýǽ]/i,
    letters: "aeiouyæ",
    left: "-.16em"
  },
  {
    sym: "\u0300",
    key: "grave",
    reNone: /[aeiouy]/i,
    reWith: /[àèìòùỳ]/i,
    letters: "aeiouy",
    left: "-.16em"
  },
  {
    sym: "\u0303",
    key: "tilde",
    reNone: /[aeinouvy]/i,
    reWith: /[ãẽĩñõũṽỹ]/i,
    letters: "aeinouvy",
    left: "-.16em"
  },
  {
    sym: "\u0306",
    key: "breve",
    reNone: /[aeiouy]/i,
    reWith: /[ăĕĭŏŭ]/i,
    letters: "aeiou",
    left: "-.16em"
  },
  {
    sym: "\u0311",
    key: "invertedBreve",
    reNone: /[aeiouy]/i,
    reWith: /[ȃȇȋȏȗ]/i,
    letters: "aeiou"
  },
  {
    sym: "\u0308",
    key: "diaeresis",
    reNone: /[aeiouy]/i,
    reWith: /[äëïöüÿ]/i,
    letters: "aeiouy",
    left: "-.16em"
  }
].map(mark => ({
  ...mark,
  reLettersWith: new RegExp(
    "(" +
      mark.reWith.source
        .slice(1, -1)
        .match(/\\(x[0-9a-f]{2}|u[0-9a-f]{4}|.)|./gi)
        .join(")|(") +
      ")",
    "gi"
  )
}));
